import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchInvoice } from '../api/resources';
import { inr, fmtDate } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import Spinner from '../components/ui/Spinner';
import Modal from '../components/ui/Modal';
import { StatusBadge } from '../components/ui/Badge';
import MeasurementValue from '../components/ui/MeasurementValue';
import { downloadPdf, openPdfForPrint } from '../utils/pdf';
import { groupBillLines } from '../utils/groupLines';
import OrderTimeline from '../components/invoice/OrderTimeline';
import { forWhom } from '../utils/orderFor';

// Status rendering lives in the shared Badge component; re-exported here
// because InvoiceWizard already imports STATUS from this module.
export const STATUS = StatusBadge;

const PAID_TYPES = ['CASH', 'CARD', 'UPI', 'PAYTM', 'WALLET'];
const PAID_LABEL = { CASH: 'Cash', CARD: 'Card', UPI: 'D-Pay/UPI', PAYTM: 'Paytm', WALLET: 'Wallet' };

export default function InvoicePreview() {
  const { id } = useParams();
  const nav = useNavigate();
  const { currentCompany } = useAuth();
  // currentCompany is the membership row — the shop itself sits inside it.
  const shop = currentCompany?.company || currentCompany;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => fetchInvoice(Number(id)),
    enabled: !!id,
  });

  const inv = data?.invoice;
  const [pdfBusy, setPdfBusy] = useState(null); // 'download' | 'print' | null
  const [pdfError, setPdfError] = useState(null);

  const runPdf = async (mode) => {
    const el = document.getElementById('invoice-print-area');
    const filename = `Invoice-${inv?.orderNo || id}.pdf`;
    try {
      setPdfError(null);
      setPdfBusy(mode);
      if (mode === 'download') {
        await downloadPdf(el, filename);
      } else {
        const { blocked } = await openPdfForPrint(el, filename);
        if (blocked) setPdfError('Popup blocked — the PDF was downloaded instead.');
      }
    } catch (e) {
      setPdfError(e.message || 'Could not generate the PDF.');
    } finally {
      setPdfBusy(null);
    }
  };

  useEffect(() => {
    if (inv) document.title = `Invoice ${inv.orderNo} — Tailoring ERP`;
    return () => { document.title = 'Tailoring ERP'; };
  }, [inv]);

  if (isLoading) return <div className="flex justify-center py-24"><Spinner label="Loading invoice…" /></div>;
  if (isError || !inv) {
    return (
      <div className="mx-auto max-w-md py-24 text-center">
        <p className="text-lg font-semibold text-content">Invoice not found</p>
        <button className="btn-primary mt-4" onClick={() => nav('/invoices')}>Back to Invoices</button>
      </div>
    );
  }

  return (
    <div className="print-reset mx-auto max-w-4xl py-6">
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-2">
        <button className="btn-ghost" onClick={() => nav(-1)}>‹ Back</button>
        <div className="flex flex-1 items-center justify-end gap-2">
          {pdfError && <span className="text-xs text-danger-500">{pdfError}</span>}
          <button
            className="btn-outline"
            disabled={!!pdfBusy}
            onClick={() => runPdf('download')}
          >
            {pdfBusy === 'download' ? 'Generating…' : '⬇ Download PDF'}
          </button>
          <button
            className="btn-accent"
            disabled={!!pdfBusy}
            onClick={() => runPdf('print')}
          >
            {pdfBusy === 'print' ? 'Preparing…' : '🖨 Print'}
          </button>
        </div>
      </div>

      {/* doc-sheet pins theme tokens to light: this is ink on paper. */}
      <div id="invoice-print-area" className="doc-sheet card overflow-hidden p-0">
        {/* Header */}
        <div className="px-4 py-5 text-white sm:px-8" style={{ backgroundColor: '#10616f' }}>
          <div className="flex flex-wrap items-start justify-between gap-4 sm:gap-6">
            <div className="min-w-0">
              <p className="text-2xl font-bold tracking-tight">{shop?.name || 'Bespoke Tailors'}</p>
              <p className="mt-1 max-w-md text-xs text-white/75">{shop?.address || 'Main Market Road, City — 000000'}</p>
              <p className="text-xs text-white/75">Ph: {shop?.phone || '—'} · GSTIN: {shop?.gstin || '—'}</p>
            </div>
            <div className="shrink-0 rounded-lg px-3 py-2 text-right sm:px-4" style={{ backgroundColor: '#fff', color: '#15424d' }}>
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#1996a8' }}>Tax Invoice</p>
              <p className="text-lg font-bold">#{inv.orderNo}</p>
              <p className="text-xs" style={{ color: '#5a6776' }}>Date: {fmtDate(inv.orderDate)}</p>
            </div>
          </div>
        </div>

        <div className="px-4 py-5 sm:px-8 sm:py-6">
          {/* From / To / meta */}
          <div className="grid gap-4 text-sm sm:grid-cols-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-content-subtle">Billed To</p>
              <p className="mt-1 font-semibold text-content">{inv.customer?.name || '—'}</p>
              {/* Whose clothes these are — the bill says so under the name
                  that pays for them. */}
              {inv.familyMember && (
                <p className="font-semibold text-brand-700">{forWhom(inv.familyMember)}</p>
              )}
              <p className="text-content-muted">{inv.customer?.mobile}</p>
              <p className="text-content-muted">{[inv.customer?.addressLine1, inv.customer?.city, inv.customer?.state].filter(Boolean).join(', ')}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-content-subtle">Order Details</p>
              <p className="mt-1 text-content-muted">Ref: {inv.reference || '—'}</p>
              <p className="text-content-muted">Delivery: {inv.deliveryDate ? fmtDate(inv.deliveryDate) : '—'}</p>
              <p className="text-content-muted">Trial: {inv.trialDate ? fmtDate(inv.trialDate) : '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-content-subtle">Status</p>
              <div className="mt-1"><STATUS status={inv.status} /></div>
              <p className="mt-2 text-content-muted">Total: <b className="text-brand-900">{inr(inv.totalAmount)}</b></p>
              <p className="text-success-600">Received: {inr(inv.receivedAmount)}</p>
              <p className="text-danger-600">Due: {inr(inv.dueAmount)}</p>
            </div>
          </div>

          {/* Items table — nine columns, so on a narrow screen it scrolls
              sideways rather than squeezing every figure illegible. Print has
              its own sheet width and is unaffected. */}
          <div className="-mx-4 mt-6 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <table className="w-full min-w-[44rem] text-sm">
            <thead>
              <tr className="bg-surface-sunken text-left text-[11px] uppercase tracking-wider text-content-muted">
                <th className="rounded-l-lg px-3 py-2">Sr</th>
                <th className="px-3 py-2">Item</th>
                <th className="px-3 py-2 text-center">Qty</th>
                <th className="px-3 py-2 text-right">Rate</th>
                <th className="px-3 py-2 text-right">Disc</th>
                <th className="px-3 py-2 text-right">Taxable</th>
                <th className="px-3 py-2 text-right">GST%</th>
                <th className="px-3 py-2 text-right">GST Amt</th>
                <th className="rounded-r-lg px-3 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {groupBillLines(inv.items || []).map((it, i) => (
                <Fragment key={it.id || i}>
                <tr className="border-b border-line">
                  <td className="px-3 py-2 text-content-subtle">{i + 1}</td>
                  <td className="px-3 py-2">
                    <p className="font-medium text-content">{it.itemName}</p>
                    {/* A set is one line with its parts named under it, so the
                        customer sees what they are paying for. */}
                    {it.isSet && (it.sources || []).length > 0 && (
                      <p className="text-xs text-content-subtle">
                        {it.sources.map((s) => s.itemName).join(' + ')}
                      </p>
                    )}
                    {it.familyMember && <p className="text-xs text-content-subtle">for {it.familyMember.name}</p>}
                    {it.salesman && <p className="text-xs text-content-subtle">by {it.salesman.name}</p>}
                    {it.alteration && <p className="text-xs font-semibold text-warning-700">Alteration</p>}
                  </td>
                  <td className="px-3 py-2 text-center">{it.qty}</td>
                  {/* A set has no rate of its own — its parts do — so the
                      column shows the set's own total instead of one part's. */}
                  <td className="px-3 py-2 text-right">{it.isSet ? inr(it.taxableAmt) : inr(it.rate)}</td>
                  <td className="px-3 py-2 text-right text-success-600">{inr(it.discountAmt)}</td>
                  <td className="px-3 py-2 text-right">{inr(it.taxableAmt)}</td>
                  <td className="px-3 py-2 text-right">{Number(it.taxRate)}%</td>
                  <td className="px-3 py-2 text-right">{inr(it.gstAmount)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{inr(it.finalAmt)}</td>
                </tr>
                {/* Measurements sit on their own full-width row so the money
                    columns above stay aligned. Garments added together on the
                    row above are still measured one by one, so each gets its
                    own line — they are rarely identical. */}
                {(it.sources || [it])
                  .filter((src) => src.printOnInvoice !== false && (src.measurements || []).length > 0)
                  .map((src, s, shown) => (
                    <tr className="border-b border-line" key={src.id || `m-${s}`}>
                      <td />
                      <td className="px-3 pb-2 text-[10px]" colSpan={8}>
                        <span className="font-bold uppercase tracking-wider text-content-subtle">
                          Measurements{shown.length > 1 ? ` · ${it.itemName} #${s + 1}` : ''}{' '}
                        </span>
                        {(src.measurements || []).map((mm, j) => (
                          <span key={mm.measurementId || j} className="mr-2 whitespace-nowrap">
                            <span className="text-content-subtle">{mm.name}</span>{' '}
                            <MeasurementValue value={mm.value} className="font-semibold text-content" />
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-line text-sm">
                <td colSpan={6} />
                <td className="px-3 py-2 text-right text-content-muted" colSpan={2}>Subtotal</td>
                <td className="px-3 py-2 text-right font-semibold">{inr(inv.subtotal)}</td>
              </tr>
              <tr>
                <td colSpan={6} />
                <td className="px-3 py-1 text-right text-content-muted" colSpan={2}>Discount</td>
                <td className="px-3 py-1 text-right font-semibold text-success-600">- {inr(inv.discountTotal)}</td>
              </tr>
              <tr>
                <td colSpan={6} />
                <td className="px-3 py-1 text-right text-content-muted" colSpan={2}>GST Total</td>
                <td className="px-3 py-1 text-right font-semibold">{inr(inv.gstTotal)}</td>
              </tr>
              {Number(inv.freightAmount) !== 0 && (
                <tr>
                  <td colSpan={6} />
                  <td className="px-3 py-1 text-right text-content-muted" colSpan={2}>Freight</td>
                  <td className="px-3 py-1 text-right font-semibold">{inr(inv.freightAmount)}</td>
                </tr>
              )}
              {Number(inv.adjustAmount) !== 0 && (
                <tr>
                  <td colSpan={6} />
                  <td className="px-3 py-1 text-right text-content-muted" colSpan={2}>Adjustment</td>
                  <td className="px-3 py-1 text-right font-semibold">{inr(inv.adjustAmount)}</td>
                </tr>
              )}
              <tr>
                <td colSpan={6} />
                <td className="rounded-tl-lg px-3 py-2 text-right text-white" colSpan={2} style={{ backgroundColor: '#10616f' }}>Grand Total</td>
                <td className="rounded-tr-lg px-3 py-2 text-right font-bold text-white" style={{ backgroundColor: '#10616f' }}>{inr(inv.totalAmount)}</td>
              </tr>
            </tfoot>
          </table>
          </div>

          {/* Amount in words */}
          <p className="mt-3 text-xs text-content-muted">
            <b className="text-content">Amount in words:</b> {amountInWords(inv.totalAmount)}
          </p>

          {/* Payments */}
          {(inv.payments || []).length > 0 && (
            <div className="mt-4 rounded-xl border border-line p-3 text-xs">
              <p className="mb-1 font-bold uppercase tracking-wider text-content-subtle">Payments Received</p>
              <table className="w-full">
                <tbody>
                  {(inv.payments || []).map((p) => (
                    <tr key={p.id} className="border-t border-line">
                      <td className="py-1">{fmtDate(p.date)}</td>
                      <td className="py-1">{PAID_LABEL[p.type] || p.type}</td>
                      <td className="py-1 text-content-subtle">{p.remark}</td>
                      <td className="py-1 text-right font-semibold">{inr(p.amount)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-line">
                    <td className="pt-1" colSpan={3}>Total Received</td>
                    <td className="pt-1 text-right font-bold text-success-600">{inr(inv.receivedAmount)}</td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="py-0.5">Balance Due</td>
                    <td className="py-0.5 text-right font-bold text-danger-600">{inr(inv.dueAmount)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Legal + signature */}
          <div className="mt-6 flex items-end justify-between gap-6">
            <p className="max-w-md text-[10px] leading-relaxed text-content-subtle">
              Goods once sold will not be taken back or exchanged. Any complaint must be made within 7 days of delivery.
              Please verify all measurements at the time of trial. We are not responsible for any damage caused by wash or dry-clean.
            </p>
            <div className="text-center">
              <div className="h-10" />
              <div className="w-44 border-t border-line-strong pt-1 text-xs text-content-muted">
                Authorised Signature — {shop?.name || ''}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Screen only — the timeline is for the shop, not the customer's bill. */}
      <div className="no-print card mt-4 p-4">
        <OrderTimeline invoiceId={inv.id} />
      </div>
    </div>
  );
}

function amountInWords(n) {
  const num = Math.floor(Number(n) || 0);
  if (num === 0) return 'Zero Rupees Only';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = (x) => (x < 20 ? ones[x] : tens[Math.floor(x / 10)] + (x % 10 ? ' ' + ones[x % 10] : ''));
  const three = (x) => (x < 100 ? two(x) : ones[Math.floor(x / 100)] + ' Hundred' + (x % 100 ? ' ' + two(x % 100) : ''));
  let words = '';
  let crore = Math.floor(num / 10000000);
  let lakh = Math.floor((num % 10000000) / 100000);
  let thousand = Math.floor((num % 100000) / 1000);
  let hundred = num % 1000;
  if (crore) words += three(crore) + ' Crore ';
  if (lakh) words += three(lakh) + ' Lakh ';
  if (thousand) words += three(thousand) + ' Thousand ';
  if (hundred) words += three(hundred);
  return words.trim() + ' Rupees Only';
}
